import {
  COMP_SUM_FETCH_SUCCESS,
  DOWNLOAD_COMPLETE,
  GET_CONFIG_PREFERENCES,
  GET_DISPLAY_PREFERENCES,
  GET_PRICING_FAILED,
  GET_PRICING_START,
  RESET_TO_INITIAL_STATE,
  SAVE_CONFIG,
  SET_DISPLAY_IN,
  SET_EMPLOYEE_DATA,
  SET_LOADING,
  SET_MARKET_RANGE_DATA,
  SET_PRICING_DATA,
  SET_CSR_SELECTED_BENCHMARK_YEAR,
  SET_VIEW_DATA,
  START_COMP_SUM_FETCH,
  START_DOWNLOADING,
  TOGGLE_CONFIG,
  TOGGLE_REPORT_SECTION,
  UPDATE_PRICING_DATA,
  CSR_SET_VIEW_NAME,
  CSR_SET_SURVEY_VIEW_NAME,
  CSR_SET_EMPLOYEE_VIEW_PK,
  CSR_SET_JOB_DETAILS_VIEW_PK,
  CSR_SET_SHOW_ZERO_WEIGHT_MATCHES
} from './CompensationSummaryActions';

import { SESSION_DATA_FETCH_SUCCESS } from '../../actions/session';
import { getIndexOfElement, getInternalAvgData, getRoundedValue } from './payElementMappings';
import { capitalizeFirstLetter } from 'ps-components/lib/formatters';
import { DISPLAY_IN_TYPE, getMonetaryRoundingFromDisplayInType } from '../../lib/displayIn';
import { PRICING_TYPE } from '../SkillGeoDifferential/SkillGeoDifferentialReducer';

const initialState = {
  data: {},
  isConfigVisible: false,
  loading: true,
  showCoverPage: true,
  showEmployeeData: true,
  showInternalData: true,
  showParticipationMatchesWithData: false,
  showParticipationMatchesWithoutData: false,
  showInternalJobDescription: true,
  showLastModified: true,
  showMarketComparison: true,
  showMarketRange: true,
  showMatchDetails: true,
  showOverallComments: true,
  showOverallComposites: true,
  showZeroWeightMatches: true,
  showSurveyJobDescription: true,
  showPricingDetails: true,
  customHeader: '',
  customFooter: '',
  reportName: 'Position Compensation Summary of',
  isDownloading: false,
  monetaryRounding: 0,
  numericRounding: 0,
  percentRounding: 0,
  showAgingFactor: false,
  showGeoAdjustment: false,
  showJobCodeKey: false,
  selectedBenchmarkYear: null,
  displayIn: DISPLAY_IN_TYPE.ANNUAL,
  getJobPricingLoading: false,
  getJobPricingFailed: false,
  getJobPricingLoaded: false,
  showWgtBy: false,
  useParticipationMatch: false,
  viewName: null,
  surveyViewName: null,
  selectedEmployeesViewPk: null,
  selectedJobDetailsViewPk: null
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GET_CONFIG_PREFERENCES:
      return {
        ...state,
        numericRounding: action.data.numericRounding !== null ? action.data.numericRounding : 2,
        percentRounding: action.data.percentRounding !== null ? action.data.percentRounding : 2,
        showAgingFactor: action.data.showAgingFactor,
        showGeoAdjustment: action.data.showGeoAdjustment,
        showJobCodeKey: action.data.showJobCodeKey,
        showWgtBy: action.data.showWgtBy,
        useParticipationMatch: action.data.useParticipationMatch
      };
    case RESET_TO_INITIAL_STATE: {
      return { ...initialState };
    }
    case GET_DISPLAY_PREFERENCES:
      return {
        ...state,
        showCoverPage: action.data.showCoverPage,
        showEmployeeData: action.data.showEmployeeData,
        showInternalData: action.data.showInternalData,
        showInternalJobDescription: action.data.showInternalJobDescription,
        showLastModified: action.data.showLastModified,
        showMarketComparison: action.data.showMarketComparison,
        showMarketRange: action.data.showMarketRange,
        showMatchDetails: action.data.showMatchDetails,
        showParticipationMatchesWithData: action.data.showParticipationMatchesWithData,
        showParticipationMatchesWithoutData: action.data.showParticipationMatchesWithoutData,
        showOverallComments: action.data.showOverallComments,
        showPricingDetails: action.data.showPricingDetails,
        showOverallComposites: action.data.showOverallComposites,
        showZeroWeightMatches: action.data.showZeroWeightMatches,
        showSurveyJobDescription: action.data.showSurveyJobDescription,
        customFooter: action.data.customFooter,
        customHeader: action.data.customHeader,
        reportName: action.data.reportName,
        showCompanyLogo: action.data.showCompanyLogo,
        showPayScaleLogo: action.data.showPayScaleLogo
      };
    case START_COMP_SUM_FETCH:
      return {
        ...state,
        loading: true
      };
    case COMP_SUM_FETCH_SUCCESS:
      return {
        ...state,
        loading: false
      };
    case SAVE_CONFIG: {
      const newState = { ...state };
      Object.entries(action.data).forEach(element => (newState[element[0]] = element[1]));

      return newState;
    }
    case SET_EMPLOYEE_DATA: {
      return {
        ...state,
        data: {
          ...state.data,
          employeeData: action.data
        }
      };
    }
    case SET_MARKET_RANGE_DATA: {
      const marketRangeData =
        action.data && Object.keys(action.data).length
          ? {
              '10th': action.data.data['10th'],
              '25th': action.data.data['25th'],
              '50th': action.data.data['50th'],
              '75th': action.data.data['75th'],
              '90th': action.data.data['90th'],
              scatterData: action.data.scatterData
            }
          : action.data;

      return {
        ...state,
        data: {
          ...state.data,
          marketRange: marketRangeData
        }
      };
    }

    case SET_PRICING_DATA: {
      const pricingColumns = action.data.fields.map(field => field.default_title);
      const pricingRows = action.data.rows;
      const pricingType = action.data.pricingType;
      const surveyJobCode = getIndexOfElement(pricingColumns, 'Survey Job Code');
      const surveyJobTitle = getIndexOfElement(pricingColumns, 'Survey Job Title');
      const slotJobTitle = getIndexOfElement(pricingColumns, 'Slot Job Title');
      const slotJobCode = getIndexOfElement(pricingColumns, 'Slot Job Code');
      const slotJobCodeKey = getIndexOfElement(pricingColumns, 'Slot Job ${job_code_key_text}');
      const surveySourceCode = getIndexOfElement(pricingColumns, 'Survey Source Code');
      const surveyJobDescription = getIndexOfElement(pricingColumns, 'Survey Job Description');
      const matchComments = getIndexOfElement(pricingColumns, 'Match Comments');
      const surveyDatacut = getIndexOfElement(pricingColumns, 'Survey Data Cut');
      const cosNum = getIndexOfElement(pricingColumns, 'Market Base Salary # of Cos');
      const eesNum = getIndexOfElement(pricingColumns, 'Market Base Salary # of Ees');
      const surveyJobCurrency = getIndexOfElement(pricingColumns, 'Survey Job Currency');
      const cutId = getIndexOfElement(pricingColumns, 'Cut ID');
      const overallAdjustment =
        pricingType !== PRICING_TYPE.SLOTTED
          ? action.data.fields.findIndex(
              element => element.default_title === 'Overall Benchmark Adjustment' && element.aggregate_code === 'avg'
            )
          : getIndexOfElement(pricingColumns, 'Slottee Overall Adjustment');
      const surveyEffectiveDate = getIndexOfElement(pricingColumns, 'Survey Effective Date');
      const surveyTitle = getIndexOfElement(pricingColumns, 'Survey Report Short Title');
      const overallComment =
        pricingType !== PRICING_TYPE.SLOTTED
          ? getIndexOfElement(pricingColumns, 'Overall Benchmark Comment')
          : getIndexOfElement(pricingColumns, 'Slottee Benchmark Comment');
      const wgt = getIndexOfElement(pricingColumns, 'Data Weighting Factor');
      const geo = getIndexOfElement(pricingColumns, 'Geo Adjustment Factor');
      const adj = getIndexOfElement(pricingColumns, 'Data Adjustment Factor');
      const agingFactor = getIndexOfElement(pricingColumns, 'Aging Factor');
      const agingCode = getIndexOfElement(pricingColumns, 'Aging Code');
      let lastModifiedName = getIndexOfElement(pricingColumns, 'Last Modified User Name');
      if (lastModifiedName === -1) {
        lastModifiedName = getIndexOfElement(pricingColumns, 'Last Modified by User');
      }
      let lastModifiedDate = getIndexOfElement(pricingColumns, 'Last Modified');
      if (lastModifiedDate === -1) {
        lastModifiedDate = getIndexOfElement(pricingColumns, 'Last Modified Date');
      }
      const dataAgedToDate = getIndexOfElement(pricingColumns, 'Age to Date');
      const matchId = getIndexOfElement(pricingColumns, 'Match ID');
      const internalAvgData = getInternalAvgData(
        action.data.aggregate,
        pricingRows,
        pricingColumns,
        state.monetaryRounding,
        state.percentRounding
      );
      const surveyJobs =
        action.data.rowCount > 0 && pricingType === PRICING_TYPE.PRICED
          ? pricingRows.map(row => {
              return {
                surveyJobCode: row[surveyJobCode],
                surveyJobTitle: row[surveyJobTitle],
                surveyCode: row[surveySourceCode],
                surveyJobDescription: row[surveyJobDescription]
              };
            })
          : [];

      const slottedJobs =
        action.data.rowCount > 0 && pricingType === PRICING_TYPE.SLOTTED
          ? pricingRows.map(row => {
              return {
                slotJobCode: row[slotJobCode],
                slotJobTitle: row[slotJobTitle],
                slotJobCodeKey: row[slotJobCodeKey]
              };
            })
          : [];

      const matchSurveyDetails =
        action.data.rowCount > 0
          ? pricingRows.map((row, index) => {
              let surveyDetails = {
                surveyJobCode: row[surveyJobCode],
                surveyJobTitle: row[surveyJobTitle],
                surveySourceCode: row[surveySourceCode],
                surveyJobDescription: row[surveyJobDescription],
                slotJobCode: row[slotJobCode],
                slotJobTitle: row[slotJobTitle],
                slotJobCodeKey: row[slotJobCodeKey],
                matchComments: row[matchComments],
                surveyDatacut: row[surveyDatacut],
                cosNum: row[cosNum],
                eesNum: row[eesNum],
                surveyJobCurrency: row[surveyJobCurrency],
                surveyEffectiveDate: row[surveyEffectiveDate],
                surveyTitle: row[surveyTitle],
                surveyColumnHeaders: action.data.nonAggregate.columnHeaders,
                surveyRows: action.data.nonAggregate.surveyData[index],
                surveyAdjustmentFactors: {},
                matchId: row[matchId],
                cutId: row[cutId]
              };

              if (row[agingFactor] >= 0 && state.showAgingFactor)
                surveyDetails.surveyAdjustmentFactors.agingFactor = row[agingFactor];
              if (row[adj] >= 0) surveyDetails.surveyAdjustmentFactors.adj = row[adj];
              if (row[geo] >= 0 && state.showGeoAdjustment) surveyDetails.surveyAdjustmentFactors.geo = row[geo];
              if (row[wgt] >= 0) surveyDetails.surveyAdjustmentFactors.wgt = row[wgt];

              return surveyDetails;
            })
          : [];

      const adjustment = pricingRows[0][overallAdjustment] || 1;

      const pricingDetails =
        action.data.rowCount > 0
          ? {
              ...state.data.pricingDetails,
              surveys: matchSurveyDetails,
              adjustment,
              overallComposite: {
                ...state.data.overallComposite,
                headerData: action.data.aggregate.columnHeaders,
                rows: action.data.aggregate.surveyData,
                avgRatioHeaderData: internalAvgData.headerData,
                avgRatioData: internalAvgData.rowData
              },
              agingCode: pricingRows[0][agingCode],
              comment: pricingRows[0][overallComment]
            }
          : {};

      const headerData =
        action.data.rowCount > 0
          ? {
              ...state.data.headerData,
              lastModifiedName: pricingRows[0][lastModifiedName],
              lastModifiedDate: pricingRows[0][lastModifiedDate],
              dataAgedToDate: pricingRows[0][dataAgedToDate]
            }
          : { ...state.data.headerData };

      return {
        ...state,
        data: {
          ...state.data,
          headerData: headerData,
          surveyJobs: surveyJobs,
          slottedJobs: slottedJobs,
          pricingDetails: pricingDetails,
          pricingType: pricingType
        },
        getJobPricingFailed: false,
        getJobPricingLoading: false,
        getJobPricingLoaded: true
      };
    }
    case SET_VIEW_DATA: {
      const columns = action.data.fields.map(field => field.default_title);
      let newInternalData = action.data.fields.map(field => {
        const indexOfElement = getIndexOfElement(columns, field.default_title);
        let element = action.data.rows.length > 0 && action.data.rows[0][indexOfElement];
        const { percentRounding, numericRounding } = state;
        const monetaryRounding = getMonetaryRoundingFromDisplayInType(action.displayIn);

        const fieldType = field.field_type_code;

        element = !element
          ? null
          : getRoundedValue(element, fieldType, monetaryRounding, percentRounding, numericRounding);

        return {
          key: field.default_title,
          value: element
        };
      });

      if (!state.showJobCodeKey) {
        const replacementValue = action.replacementTokens.find(replacement => replacement.token === 'job_code_key_text')
          .value;

        // MPE-5608: Lodash cleanup, findIndex
        const jobCodeKeyIndex = newInternalData.findIndex(item => item.key === replacementValue);

        newInternalData = newInternalData.filter((datum, index) => index !== jobCodeKeyIndex);
      }

      return {
        ...state,
        data: {
          ...state.data,
          internalData: newInternalData
        }
      };
    }
    case TOGGLE_CONFIG:
      return {
        ...state,
        isConfigVisible: !state.isConfigVisible
      };
    case TOGGLE_REPORT_SECTION:
      return {
        ...state,
        [`show${capitalizeFirstLetter(action.data)}`]: !state[`show${capitalizeFirstLetter(action.data)}`]
      };
    case START_DOWNLOADING:
      return {
        ...state,
        isDownloading: true
      };
    case DOWNLOAD_COMPLETE:
      return {
        ...state,
        isDownloading: false
      };
    case SET_CSR_SELECTED_BENCHMARK_YEAR:
      return {
        ...state,
        selectedBenchmarkYear: action.benchmarkYear
      };
    case SET_DISPLAY_IN: {
      // MPE-4073: Honor the "Display Values In" selection,
      // Which also has it's own default monetary rounding
      const displayIn = action.displayIn;
      const monetaryRounding = getMonetaryRoundingFromDisplayInType(displayIn);

      return {
        ...state,
        monetaryRounding,
        displayIn
      };
    }
    case UPDATE_PRICING_DATA: {
      return {
        ...state,
        data: action.data
      };
    }
    case SET_LOADING:
      return {
        ...state,
        loading: true
      };
    case GET_PRICING_START: {
      return {
        ...state,
        getJobPricingLoading: true,
        getJobPricingLoaded: false
      };
    }
    case GET_PRICING_FAILED: {
      return {
        ...state,
        getJobPricingLoading: false,
        getJobPricingFailed: true,
        getJobPricingLoaded: false
      };
    }
    case CSR_SET_VIEW_NAME: {
      return {
        ...state,
        viewName: action.viewName
      };
    }
    case CSR_SET_SURVEY_VIEW_NAME: {
      return {
        ...state,
        surveyViewName: action.viewName
      };
    }
    case CSR_SET_EMPLOYEE_VIEW_PK: {
      return {
        ...state,
        selectedEmployeesViewPk: action.selectedEmployeesViewPk
      };
    }
    case CSR_SET_JOB_DETAILS_VIEW_PK: {
      return {
        ...state,
        selectedJobDetailsViewPk: action.selectedJobDetailsViewPk
      };
    }
    case CSR_SET_SHOW_ZERO_WEIGHT_MATCHES: {
      return {
        ...state,
        showZeroWeightMatches: action.showZeroWeightMatches
      };
    }
    case SESSION_DATA_FETCH_SUCCESS: {
      return {
        ...state,
        displayIn: action.data.monetary_display ? action.data.monetary_display : state.displayIn
      };
    }
    default:
      return { ...state };
  }
}

export const compensationSummaryActions = {};
